import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PRODUCT_IMAGE } from "@/data";

const EASE = [0.22, 1, 0.36, 1];

function Line({ children, delay }) {
  return (
    <span className="vf-line">
      <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay, ease: EASE }}>{children}</motion.span>
    </span>
  );
}

export default function Hero({ onDiscover }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const wordY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [3.5, -3.5]), { stiffness: 50, damping: 14 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-3.5, 3.5]), { stiffness: 50, damping: 14 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section className="vf-hero" id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section">
      <motion.span className="vf-hero-word" style={{ y: wordY }} aria-hidden="true">VIBEFIT</motion.span>
      <div className="vf-hero-copy">
        <motion.p className="vf-kicker" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>Plateforme vibrante · Nouvelle génération</motion.p>
        <h1 className="vf-hero-title">
          <Line delay={0.15}>Votre <em>corps.</em></Line>
          <Line delay={0.28}>Votre <em>rythme.</em></Line>
          <Line delay={0.41}>Votre <em>maison.</em></Line>
        </h1>
        <motion.p className="vf-hero-sub" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6, ease: EASE }}>
          Tonifiez votre silhouette, relancez votre circulation et offrez-vous un vrai moment de relaxation — dix minutes par jour suffisent, sans quitter votre salon.
        </motion.p>
        <motion.div className="vf-hero-ctas" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease: EASE }}>
          <button className="vf-btn-primary" onClick={onDiscover} data-testid="hero-discover-button">Découvrir la plateforme <ArrowRight size={16} /></button>
          <a className="vf-btn-ghost" href="#reviews" data-testid="hero-reviews-link"><span className="vf-stars">★★★★★</span> 4,7/5 · 184 avis</a>
        </motion.div>
        <motion.div className="vf-hero-meta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.95 }}>
          <span><b>50</b> niveaux de vibration</span>
          <span><b>10 min</b> par jour</span>
          <span><b>2 ans</b> de garantie</span>
        </motion.div>
      </div>
      <motion.div className="vf-hero-visual" style={{ rotateX, rotateY, transformPerspective: 900 }}>
        <motion.div className="vf-arch" initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.2, delay: 0.35, ease: EASE }}>
          <motion.img src={PRODUCT_IMAGE} alt="Plateforme vibrante VibeFit" style={{ y: imgY }} initial={{ scale: 1.25 }} animate={{ scale: 1.12 }} transition={{ duration: 1.6, delay: 0.35, ease: EASE }} data-testid="hero-product-image" />
          <span className="vf-arch-frame" aria-hidden="true" />
        </motion.div>
        <motion.div className="vf-hero-card" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.05, ease: EASE }} data-testid="hero-price-card">
          <span className="vf-hero-card-label">À partir de</span>
          <strong>149 €</strong>
          <span className="vf-hero-card-note">Livraison offerte · Retours 30 jours</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
